import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import Layout from 'src/components/Layout';
import Navbar from 'src/components/NavBar';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import SelectCompany from 'src/components/SelectCompany';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useGuideMe from 'src/hooks/useGuideMe';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useYearView from 'src/hooks/useYearView';
import { initialYearCheckbookDataState } from 'src/sections/view_checkbook/ViewCheckbookContext';
import useGLCodeBudgetSaveApi, {
  GLCodeBudgetSaveRequest,
} from 'src/services/useGLCodeBudgetSaveApi';
import { Company } from 'src/types/Companies';
import { generateRegexPattern } from 'src/utils/constants';
import useGuideMeSteps from './GuideMeStepsYear';
import ViewCheckbookPeriodWarningTable from './ViewCheckbookPeriodWarningTable';
import ViewCheckbookTransactionLog from './ViewCheckbookTransactionLog';

import ImportExport from './ImportExportButton';
import ViewAllAttributeUserAssignments from './ViewAllAttributeUserAssignments';

const ViewYearCheckbookNavbar = () => {
  const {
    isEditMode,
    setIsEditMode,
    selectedCompany,
    yearErrorList,
    yearWarningList,
    hasYearErrors,
    hasYearWarnings,
    setYearCheckbookDataState,
    setSelectedYearDepartment,
    departmentList,
    refreshGLCodePeriodTable,
    isYearTableDirty,
    confirmBeforeProceeding,
    pageTitle,
    setEditingGlCodeYearBudget,
    setOriginalGlCodeYearBudget,
    editingBudgetYearDataWithErrors,
    glCodeDepartments,
    modalStepCount,
    isModalOpen,
    handleCloseModal,
    hasRequireGlCodeFormatsPermission,
    editingGlCodeYearBudget,
    regexPatternBE,
    setGlCodeWarningsColumnData,
    originalGlCodeYearBudget,
    glCodeWarningsColumnData,
    openWarningModal,
    isYearEditable,
  } = useViewCheckbook();

  const { yearViewData } = useYearView();

  const { saveGlCodeBudget, isLoading } = useGLCodeBudgetSaveApi(selectedCompany.companyId);
  const { company, changeCompany } = useCompany();
  const guideMeSteps = useGuideMeSteps();
  const { confirm } = useConfirm();
  const { guideMe, openGuideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'view-budget',
  });

  useEffect(() => {
    if (company?.companyId) {
      openGuideMe();
    }
  }, [company?.companyId, openGuideMe]);

  const handleCompanyChange = useCallback(
    (company: Company) => {
      confirmBeforeProceeding(() => {
        changeCompany(company);
        setIsEditMode(false);
        setSelectedYearDepartment(departmentList[0]);
        setYearCheckbookDataState(initialYearCheckbookDataState);
      });
    },
    [
      changeCompany,
      confirmBeforeProceeding,
      departmentList,
      setIsEditMode,
      setSelectedYearDepartment,
      setYearCheckbookDataState,
    ]
  );

  const onEditClick = useCallback(() => {
    setIsEditMode(true);
    setEditingGlCodeYearBudget(yearViewData);
    setOriginalGlCodeYearBudget(yearViewData);
  }, [setEditingGlCodeYearBudget, setIsEditMode, setOriginalGlCodeYearBudget, yearViewData]);

  const handleSaveChange = useCallback(async () => {
    let updatedWarnings = [];
    if (hasRequireGlCodeFormatsPermission) {
      const activeItems = editingGlCodeYearBudget?.filter((el: any) => el.isActive);

      if (activeItems) {
        updatedWarnings = activeItems.filter((item: any) => {
          const regex = generateRegexPattern(regexPatternBE, item.code);
          return !regex;
        });
        setGlCodeWarningsColumnData(updatedWarnings);
      }
    }
    const unDeletedSaveData = editingBudgetYearDataWithErrors.filter((code) => !code.isDeleted);
    let isNewGlCodeExists = unDeletedSaveData.some((code) => code.isNewGlCode);
    let saveData: GLCodeBudgetSaveRequest[] = editingBudgetYearDataWithErrors
      .filter(
        (code) =>
          !code.isDeleted &&
          code.warnings?.warnings.some((warning) => warning.includes('unsaved changes'))
      )
      .map((item, index) => {
        const { id, code, description, isActive, isFixed, periodBudgets, isNewGlCode } = item;
        const regex = generateRegexPattern(regexPatternBE, item.code);
        const isFormattedGLCode = regex;
        const isActiveOrginal = originalGlCodeYearBudget[index]?.isActive;
        if (isNewGlCode) {
          isNewGlCodeExists = true;
        }
        const newItem: GLCodeBudgetSaveRequest = {
          id,
          code: code.trim().toUpperCase(),
          description: description.trim(),
          isActive:
            (!hasRequireGlCodeFormatsPermission ||
            (hasRequireGlCodeFormatsPermission && isFormattedGLCode) ||
            (!isActive && hasRequireGlCodeFormatsPermission && !isFormattedGLCode)
              ? isActive
              : isActiveOrginal) || false,
          isFixed,
          periodBudgets,
        };
        return newItem;
      });

    saveGlCodeBudget(saveData).then(() => {
      refreshGLCodePeriodTable();
      setIsEditMode(false);
      setYearCheckbookDataState((prev) => ({ ...prev, sort: [{ field: 'code', dir: 'asc' }] }));
      if (glCodeDepartments.clientHasDepartment && isNewGlCodeExists) {
        confirm({
          title: 'GL Code(s) Successfully Updated',
          content: (
            <div>
              GL Codes have been updated successfully. Newly created GL Codes will need to be
              assigned to your department to be viewable in View Checkbook. If you do not have
              access to add codes to departments, please contact your administrator to add them for
              you.
            </div>
          ),
          cancelButtonText: 'Ok',
          hasNoConfirmButton: true,
          width: '600px',
        });
      } else {
        toast.success('GL Code(s) has been successfully updated. Table sort set back to default.');
      }
      if (updatedWarnings?.length) {
        openWarningModal();
      }
    });
  }, [
    confirm,
    editingGlCodeYearBudget,
    editingBudgetYearDataWithErrors,
    glCodeDepartments.clientHasDepartment,
    hasRequireGlCodeFormatsPermission,
    openWarningModal,
    originalGlCodeYearBudget,
    refreshGLCodePeriodTable,
    regexPatternBE,
    saveGlCodeBudget,
    setGlCodeWarningsColumnData,
    setIsEditMode,
    setYearCheckbookDataState,
  ]);

  const handleCancelChange = useCallback(async () => {
    setEditingGlCodeYearBudget([]);
    setOriginalGlCodeYearBudget([]);
    setIsEditMode(false);
    setYearCheckbookDataState((prev) => ({ ...prev, sort: [{ field: 'code', dir: 'asc' }] }));
  }, [
    setEditingGlCodeYearBudget,
    setOriginalGlCodeYearBudget,
    setIsEditMode,
    setYearCheckbookDataState,
  ]);

  const modalSteps = useMemo(
    () => [
      {
        title: 'Warning',
        content: (
          <>
            <span className="text-sm ">
              The code(s) below could not be activated because they do not meet the required GL Code
              format(s). All other changes were saved successfully.
            </span>

            <ViewCheckbookPeriodWarningTable glCodeWarningsColumnData={glCodeWarningsColumnData} />
          </>
        ),
        isConfirmationModal: true,
      },
    ],
    [glCodeWarningsColumnData]
  );

  const handleConfirm = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  return (
    <>
      <Navbar title={pageTitle} />
      <Layout>
        <div className="flex justify-between items-center flex-wrap min-h-[56px]">
          <div className="w-[400px]">
            <SelectCompany
              className="items-center"
              onChange={handleCompanyChange}
              isControlled
              selectSuperAdmin
              attributesNeeded
            />
          </div>
          <div className="flex flex-grow items-center justify-center flex-row ">
            {!!selectedCompany?.companyId && (
              <>
                {isYearEditable ? (
                  isEditMode ? (
                    <div className="flex gap-1">
                      <PopUp
                        popupAlign={{ horizontal: 'center', vertical: 'top' }}
                        content={
                          <div className="m-1 max-w-[350px]">
                            <PopUpContentWrapper type="Error" contents={[...yearErrorList]} />
                            <PopUpContentWrapper type="Warning" contents={[...yearWarningList]} />
                          </div>
                        }
                        className="max-h-[200px]"
                        show={hasYearErrors || hasYearWarnings}
                      >
                        <Button
                          onClick={handleSaveChange}
                          disabled={hasYearErrors || !isYearTableDirty}
                          loading={isLoading}
                          color="primary"
                        >
                          Save
                        </Button>
                      </PopUp>
                      <Button color="error" onClick={handleCancelChange}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button color="primary" onClick={onEditClick}>
                      Edit
                    </Button>
                  )
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
          <div className="flex flex-row flex-wrap justify-end">
            {!!selectedCompany?.companyId && (
              <>
                <ViewAllAttributeUserAssignments />
                <ViewCheckbookTransactionLog />
                <ImportExport />

                {guideMe}
              </>
            )}
          </div>
        </div>
      </Layout>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title={modalSteps?.[modalStepCount]?.title}
        id="modal"
        width="50%"
        className="max-h-[90vh]"
      >
        {modalSteps?.[modalStepCount]?.content}
        <DialogActionsBar>
          <div className="flex justify-end mr-5">
            <Button
              color="info"
              onClick={handleConfirm}
              className={modalSteps?.[modalStepCount]?.isConfirmationModal ? 'ml-10' : ''}
            >
              {modalSteps?.[modalStepCount]?.isConfirmationModal ? 'OK' : 'Cancel'}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default ViewYearCheckbookNavbar;
